// Register or edit one asset (the Assets list's "Register asset", the asset page's Edit and "+ Add"), and the state
// chip both pages show — the live state, or which stage of removal from service.
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canManage, useSession } from '../../auth/session';
import { type AssetOverview, keys, useCategories, useSites } from '../../data/queries';
import { supabase } from '../../lib/supabase';
import { retireStage } from '../AssetRegister';
import { StateChip } from '../StateChip';

const CODE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,39}$/;

interface AssetForm {
  asset_code: string;
  category_id: string;
  description: string;
  make_model: string;
  serial: string;
  custody_site_id: string;
}

export function AssetDialog({
  open,
  onClose,
  initial,
  focus,
}: {
  open: boolean;
  onClose(): void;
  initial?: AssetOverview;
  /** the field to put the cursor in (the asset page's "+ Add" links) */
  focus?: 'make_model' | 'serial';
}) {
  const { t } = useTranslation();
  const { me } = useSession();
  const queryClient = useQueryClient();
  const categories = useCategories();
  const sites = useSites();
  const [form, setForm] = useState<AssetForm>({
    asset_code: initial?.asset_code ?? '',
    category_id: initial?.category_id ?? '',
    description: initial?.description ?? '',
    make_model: initial?.make_model ?? '',
    serial: initial?.serial ?? '',
    custody_site_id: initial?.custody_site_id ?? '',
  });
  const save = useMutation({
    mutationFn: async () => {
      const row = {
        category_id: form.category_id || null,
        description: form.description.trim() || null,
        make_model: form.make_model.trim() || null,
        serial: form.serial.trim() || null,
        ...(canManage(me) || !initial ? { custody_site_id: form.custody_site_id || null } : {}),
      };
      const { error } = initial
        ? await supabase().from('assets').update(row).eq('id', initial.asset_id)
        : await supabase()
            .from('assets')
            .insert({ ...row, asset_code: form.asset_code.trim(), company_id: me!.company!.id });
      if (error) throw new Error(error.code === '23505' ? t('assets.code_taken') : error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      if (initial) void queryClient.invalidateQueries({ queryKey: keys.asset(initial.asset_id) });
      onClose();
    },
  });
  const set = (k: keyof AssetForm) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const codeOk = !!initial || CODE.test(form.asset_code.trim());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (codeOk) save.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>
          {initial ? `${t('common.edit')} ${initial.asset_code}` : t('assets.new')}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t('assets.code')}
              value={form.asset_code}
              onChange={set('asset_code')}
              disabled={!!initial}
              required
              error={!!form.asset_code && !codeOk}
              helperText={t('assets.code_help')}
              autoFocus={!initial}
            />
            <TextField
              select
              label={t('assets.category')}
              value={form.category_id}
              onChange={set('category_id')}
            >
              <MenuItem value="">—</MenuItem>
              {(categories.data ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('assets.description')}
              value={form.description}
              onChange={set('description')}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('assets.make_model')}
                value={form.make_model}
                onChange={set('make_model')}
                autoFocus={focus === 'make_model'}
                fullWidth
              />
              <TextField
                label={t('assets.serial')}
                value={form.serial}
                onChange={set('serial')}
                autoFocus={focus === 'serial'}
                fullWidth
              />
            </Stack>
            {(!initial || canManage(me)) && (
              <TextField
                select
                label={`${t('assets.custody_site')} (${t('common.optional')})`}
                value={form.custody_site_id}
                onChange={set('custody_site_id')}
              >
                <MenuItem value="">—</MenuItem>
                {(sites.data ?? [])
                  .filter((s) => s.is_active || s.site_id === initial?.custody_site_id)
                  .map((s) => (
                    <MenuItem key={s.site_id} value={s.site_id}>
                      {s.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
            {save.error && <Alert severity="error">{(save.error as Error).message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={save.isPending || !codeOk}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** The live state, or — once removed from service — which stage of it (CR_20260920_Asset_Register). */
export function AssetStateChip({ a, detail }: { a: AssetOverview; detail?: string | null }) {
  const { t } = useTranslation();
  const stage = retireStage(a);
  if (stage === 'active') return <StateChip state={a.display_state} detail={detail} />;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={stage === 'awaiting_tag_removal' ? 'warning' : 'default'}
      label={t(
        stage === 'awaiting_tag_removal' ? 'retire.filter_awaiting' : 'retire.filter_retired',
      )}
    />
  );
}
